import { Button, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../api/adminApi";
import { pageRoutes } from "../../../../routes";
import type { GeneralInboxSettingsForm } from "./inboxSettingsValidation";
import { validateGeneralInboxSettingsForm } from "./inboxSettingsValidation";

interface Props {
  onClose: () => void;
}

const createInbox = withErrorHandling(adminApi.createInbox);

const CreateAgentInboxDialog = ({ onClose }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const navigate = useNavigate();

  const [form, setForm] = useState<GeneralInboxSettingsForm>({
    name: "",
    description: "",
    touchedFields: [],
  });

  const [loading, setLoading] = useState(false);

  const handleCreateClick = async () => {
    setLoading(true);
    const [newInbox, error] = await createInbox({ name: form.name.trim(), description: form.description.trim() });
    setLoading(false);

    if (error) {
      logError(error, "[CreateConfigurationDialog] handleCreateClick");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Agent inbox created successfully");
    navigate(`${pageRoutes.agentInboxes}/${newInbox.id}`);
  };

  const validationResult = validateGeneralInboxSettingsForm(form);

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>New Agent Inbox</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value, touchedFields: distinct([...form.touchedFields, "name"]) })
            }
            error={form.touchedFields.includes("name") && !validationResult.name?.isValid}
            helperText={form.touchedFields.includes("name") ? validationResult.name?.error : undefined}
          />
          <TextField
            fullWidth
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description: e.target.value,
                touchedFields: distinct([...form.touchedFields, "description"]),
              })
            }
            error={form.touchedFields.includes("description") && !validationResult.description?.isValid}
            helperText={form.touchedFields.includes("description") ? validationResult.description?.error : undefined}
          />
        </Stack>
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleCreateClick} disabled={!validationResult.isValid}>
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CreateAgentInboxDialog;
